import type { SpellingMode, WordListId } from './types.js';

/**
 * Word validity lookup.
 *
 * Kept behind an interface so the engine never cares where the words came
 * from: a bundled public-domain list in the browser, the same list in the
 * Durable Object, or a stub in tests. `has` must be synchronous and fast —
 * it is called for every run on every keystroke in live-feedback mode.
 */
export interface Dictionary {
  readonly id: WordListId;
  readonly name: string;
  readonly size: number;
  has(word: string): boolean;
}

/** Normalise to the form stored in every list: upper case, letters only. */
export function normalise(word: string): string {
  return word.toUpperCase().replace(/[^A-Z]/g, '');
}

/** A plain Set-backed dictionary. Roughly 12 MB of heap for ENABLE. */
export class SetDictionary implements Dictionary {
  readonly id: WordListId;
  readonly name: string;
  readonly #words: Set<string>;

  constructor(id: WordListId, name: string, words: Iterable<string>) {
    this.id = id;
    this.name = name;
    this.#words = new Set<string>();
    for (const w of words) {
      const n = normalise(w);
      if (n.length >= 2) this.#words.add(n);
    }
  }

  get size(): number { return this.#words.size; }
  has(word: string): boolean { return this.#words.has(normalise(word)); }
}

/**
 * Two lists at once: a word is valid if either accepts it.
 * Lets a table play "family rules" (friendly list) without losing the
 * long tail that experienced players expect.
 */
export class UnionDictionary implements Dictionary {
  constructor(
    readonly id: WordListId,
    readonly name: string,
    private readonly parts: readonly Dictionary[],
  ) {}

  get size(): number { return this.parts.reduce((a, d) => a + d.size, 0); }
  has(word: string): boolean { return this.parts.some((d) => d.has(word)); }
}

/**
 * British spelling of a word and its American counterpart, from SCOWL.
 * `americanOnly` lists the counterparts British English does not use, so
 * that "realized" or "judgment" — both fine in Britain — are never refused.
 */
export interface SpellingData {
  /** British-only spelling → American spelling, upper case. */
  pairs: Readonly<Record<string, string>>;
  /** American spellings that are not British. */
  americanOnly: readonly string[];
}

/**
 * A word list with a choice of spelling.
 *
 * The lists the game ships are a mixture: ENABLE has "colour" but not
 * "favourite". So the spelling setting only ever swaps the spelling of a word
 * the list already knows, never adds new words:
 *  - a British-only spelling is accepted if the list has the word in either
 *    spelling (unless the table plays American);
 *  - an American-only spelling likewise (unless the table plays British);
 *  - every other word is just the list's verdict.
 */
export class SpellingDictionary implements Dictionary {
  readonly #toAmerican: ReadonlyMap<string, string>;
  readonly #toBritish: ReadonlyMap<string, string[]>;

  constructor(
    private readonly base: Dictionary,
    data: SpellingData,
    readonly mode: SpellingMode,
  ) {
    this.#toAmerican = new Map(Object.entries(data.pairs));
    const american = new Set(data.americanOnly);
    const toBritish = new Map<string, string[]>();
    for (const [uk, us] of Object.entries(data.pairs)) {
      if (!american.has(us)) continue;
      const list = toBritish.get(us);
      if (list) list.push(uk); else toBritish.set(us, [uk]);
    }
    this.#toBritish = toBritish;
  }

  get id(): WordListId { return this.base.id; }
  get name(): string { return this.base.name; }
  get size(): number { return this.base.size; }

  has(word: string): boolean {
    const w = normalise(word);
    const us = this.#toAmerican.get(w);
    if (us !== undefined) {
      if (this.mode === 'american') return false;
      return this.base.has(w) || this.base.has(us);
    }
    const uks = this.#toBritish.get(w);
    if (uks !== undefined) {
      if (this.mode === 'british') return false;
      return this.base.has(w) || uks.some((u) => this.base.has(u));
    }
    return this.base.has(w);
  }
}

/** Always answers "I don't know" — used before the word pack has loaded. */
export const PENDING_DICTIONARY: Dictionary = {
  id: 'custom',
  name: 'loading',
  size: 0,
  has: () => false,
};
